import React, { useState, useRef, useEffect } from "react";
import { AudioFilled, XFilled } from "@ant-design/icons";
import { Button } from "antd";
import Logger from "@/utility/logger";
import { convertToWav } from "@/utility/audioWav";

interface VoiceRecorderProps {
  onTranscriptionComplete: (text: string) => void;
  disabled?: boolean;
  className?: string;
  maxDuration?: number; // In seconds
  setShowError?: (object: any) => void;
  titleMap?: Record<string, string>;
  transcribeAudio: (audioBlob: Blob) => Promise<string>;
}

function VoiceRecorder({
  onTranscriptionComplete,
  disabled = false,
  className = "",
  maxDuration = 30,
  setShowError,
  titleMap,
  transcribeAudio,
}: VoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Cleanup function
  const cleanup = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    audioChunksRef.current = [];
    setRecordingTime(0);
    setError(null);
  };

  // Start recording
  const startRecording = async () => {
    try {
      setError(null);

      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      streamRef.current = stream;
      audioChunksRef.current = [];

      // Create MediaRecorder
      let options = { mimeType: "" };

      if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        options.mimeType = "audio/webm;codecs=opus";
      } else if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) {
        options.mimeType = "audio/ogg;codecs=opus";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        options.mimeType = "audio/mp4";
      }

      Logger.debug("VoiceRecorder.tsx: Using mimeType for MediaRecorder:", options.mimeType);

      const mediaRecorder = new MediaRecorder(stream, options);

      mediaRecorderRef.current = mediaRecorder;

      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        handleRecordingComplete();
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          if (newTime >= maxDuration) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);
    } catch (error) {
      Logger.error("VoiceRecorder.tsx: Error starting recording:", error);
      setError("Failed to access microphone. Please check permissions.");
      //   setShowError({
      //     isVisible: true,
      //     type: "error",
      //     title: titleMap[error.name],
      //     message: error.name,
      //     details: {
      //       message: error.message,
      //       code: error.code,
      //       stack: error.stack,
      //       context:
      //         error.name === "RichApi.Error"
      //           ? {
      //               errorLocation: error.debugInfo?.errorLocation,
      //               statement: error.debugInfo?.statement,
      //               surroundingStatements: error.debugInfo?.surroundingStatements,
      //             }
      //           : undefined,
      //     },
      //   });
      cleanup();
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    }
  };

  // Handle recording completion
  const handleRecordingComplete = async () => {
    if (audioChunksRef.current.length === 0) {
      setError("No audio data recorded");
      cleanup();
      return;
    }

    setIsTranscribing(true);

    try {
      // Create audio blob using recorded container format
      const containerType = audioChunksRef.current[0]?.type || "audio/webm;codecs=opus";
      const audioBlob = new Blob(audioChunksRef.current, { type: containerType });

      // Convert to WAV for better compatibility
      const audioBuffer = await audioBlob.arrayBuffer();
      const wavBlob = await convertToWav(audioBuffer);

      // Transcribe audio
      const transcription = await transcribeAudio(wavBlob);

      if (!(transcription == "")) {
        onTranscriptionComplete(transcription);
      } else {
        setError("No speech detected in recording");
      }
    } catch (error) {
      Logger.error("VoiceRecorder.tsx: Error processing recording:", error);
      setShowError({
        isVisible: true,
        type: "error",
        title: titleMap[error.name],
        message: error.name,
        details: {
          message: error.message,
          code: error.code,
          stack: error.stack,
          context:
            error.name === "RichApi.Error"
              ? {
                  errorLocation: error.debugInfo?.errorLocation,
                  statement: error.debugInfo?.statement,
                  surroundingStatements: error.debugInfo?.surroundingStatements,
                }
              : undefined,
        },
      });
      setError("Failed to transcribe audio");
    } finally {
      setIsTranscribing(false);
      cleanup();
    }
  };

  // Handle button click
  const handleClick = () => {
    if (disabled || isTranscribing) return;

    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Format time display
  const formatTime = (seconds: number) => {
    return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, "0")}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const getButtonContent = () => {
    if (isTranscribing) {
      return <></>;
    }

    if (isRecording) {
      return <XFilled style={{ fontSize: "1rem" }} />;
    }

    return <AudioFilled style={{ fontSize: "1rem" }} />;
  };

  return (
    <div className="voice-recorder-container">
      <Button
        onClick={handleClick}
        disabled={disabled || isTranscribing}
        className={`voice-recorder-button ${isRecording ? "recording" : ""} ${
          isTranscribing ? "transcribing" : ""
        } ${className}`}
        title={
          isTranscribing
            ? "Transcribing..."
            : isRecording
            ? "Click to stop recording"
            : "Click to start voice recording"
        }
      >
        {getButtonContent()}
      </Button>

      {/* Recording indicator */}
      {isRecording && (
        <div className="voice-recorder-indicator">
          <div className="voice-recorder-time">
            {formatTime(recordingTime)} / {formatTime(maxDuration)}
          </div>
          <div className="voice-recorder-pulse">
            <div className="voice-recorder-pulse-dot"></div>
          </div>
        </div>
      )}

      {/* Transcribing indicator */}
      {isTranscribing && <div className="voice-recorder-status">Transcribing...</div>}

      {/* Error display */}
      {error && <div className="voice-recorder-error">{error}</div>}
    </div>
  );
}

export default VoiceRecorder;
